export const content = {
    en: {
        nav: {
            items: [
                { label: "About", href: "#about" },
                { label: "Products", href: "#products" },
                { label: "Why Sybrax", href: "#why-sybrax" },
                { label: "Roadmap", href: "#roadmap" },
                { label: "Team", href: "#team" },
                { label: "Contact", href: "#contact" },
            ],
            cta: "Get in touch",
        },
        hero: {
            eyebrow: "AI product studio · Istanbul",
            title: "Sybrax",
            tagline: "AI-powered software and digital product studio.",
            description:
                "Sybrax is a next-generation technology venture building AI-powered digital products. Our first products, Mooddy and uSeen, have reached MVP stage, with Promlock and CodUpp joining the portfolio soon.",
            ctaPrimary: "Explore Products",
            ctaSecondary: "Contact Us",
            emphasis: "Built for AI, SaaS, social products and digital games.",
        },
        stats: [
            { value: "4", label: "Ready" },
            { value: "2", label: "In Pipeline" },
            { value: null, label: "AI / SaaS / Social / Game" },
            { value: null, label: "Global focus" },
        ],
        about: {
            label: "01 — About",
            title: "About Sybrax",
            note: "Built by an experienced founding team with proven product and market exposure.",
            p1: "Sybrax is a new software and technology company being built on the product development, market experience and entrepreneurial background that comes from SearchandWork, a previously funded and still operational venture.",
            p2: "SearchandWork connects university students with employers, enabling access to daily and seasonal job opportunities through a dedicated support and employment platform. Sybrax builds on this experience with a focus on AI-native software products, SaaS platforms, mobile applications, social digital products and digital game development.",
            p3: "Sybrax's goal is to become a flexible, resilient technology company that develops globally relevant digital products across multiple categories — without being tied to a single product line, and in step with the evolution of artificial intelligence.",
        },
        products: {
            label: "02 — Portfolio",
            title: "Product Portfolio",
            intro: "Sybrax develops AI-powered platforms, social products and digital games with global potential.",
            statuses: {
                ready: "Ready",
                inDevelopment: "In Development",
                comingSoon: "Coming Soon",
            },
            items: [
                {
                    slug: "mooddy",
                    name: "Mooddy",
                    statusKey: "ready",
                    title: "AI-powered room, decision and productivity platform.",
                    description:
                        "Mooddy is a room-based productivity platform that uses AI to summarize multi-user conversations, surface decisions, manage open questions, track action items and organize knowledge. It turns scattered group discussions into trackable decisions and actions.",
                    link: { href: "https://mooddy.app", label: "Visit Mooddy" },
                },
                {
                    slug: "useen",
                    name: "uSeen",
                    statusKey: "ready",
                    title: "AI visibility and optimization platform.",
                    description:
                        "uSeen analyzes how visible brands and businesses are inside AI engines, scores their presence, and generates improvement tasks. In the next phase, it is designed to evolve into an agent that can execute selected tasks on the user's behalf with permission.",
                    link: { href: "https://useen.ai", label: "Visit uSeen" },
                },
                {
                    slug: "work-analyse",
                    name: "Work Analyse",
                    statusKey: "ready",
                    title: "AI-powered workforce optimization platform.",
                    description:
                        "AI-powered workforce optimization platform that analyzes staffing needs, workload patterns and employment models to identify the most efficient workforce structure and potential cost advantages.",
                    link: { href: "https://workanalyse.com", label: "Visit Work Analyse" },
                },
                {
                    slug: "abonelikiptal",
                    name: "Abonelikiptal.com",
                    statusKey: "ready",
                    title: "Subscription and membership cancellation platform.",
                    description:
                        "A digital platform that simplifies subscription and membership cancellation processes, helping users understand and complete cancellation steps quickly and clearly.",
                    link: { href: "https://abonelikiptal.com", label: "Visit Abonelikiptal.com" },
                },
                {
                    slug: "codupp",
                    name: "CodUpp",
                    statusKey: "inDevelopment",
                    title: "AI-powered Zero-to-Site platform.",
                    description:
                        "AI-powered Zero-to-Site platform that turns a simple business brief into a professional, ready-to-launch website through a guided conversational experience.",
                },
                {
                    slug: "promlock",
                    name: "Promlock",
                    statusKey: "comingSoon",
                    title: "Social promise and accountability platform.",
                    description:
                        "Promlock lets users record, share and prove the promises they make, turning personal commitments into socially trackable, verifiable moments. Product development is actively in progress.",
                },
            ],
        },
        why: {
            label: "03 — Why Sybrax",
            title: "Why Sybrax?",
            intro: "Sybrax is not positioned as a single-product idea. It is designed as a product studio capable of building, testing and commercializing AI-powered digital products across multiple verticals.",
            features: [
                {
                    key: "portfolio",
                    title: "MVP-level product portfolio",
                    description:
                        "Two products already at MVP maturity with active development pipelines extending the portfolio.",
                },
                {
                    key: "ai-saas",
                    title: "AI and SaaS focused structure",
                    description:
                        "Company architecture built around AI-native software, SaaS economics and recurring revenue models.",
                },
                {
                    key: "speed",
                    title: "Fast company formation and commercialization",
                    description:
                        "Lean operational setup designed to move from concept to revenue with minimal organizational drag.",
                },
                {
                    key: "global",
                    title: "Global market potential",
                    description:
                        "Products are developed with scalable market potential in mind, without being limited to a specific geography.",
                },
            ],
        },
        roadmap: {
            label: "04 — Roadmap",
            title: "Roadmap",
            intro: "A clear path from company formation to global market traction.",
            stages: [
                {
                    phase: "Phase 01",
                    title: "Company formation",
                    description:
                        "Sybrax company formation and Cube Incubation / Teknopark application process.",
                },
                {
                    phase: "Phase 02",
                    title: "User testing & commercialization prep",
                    description:
                        "Mooddy and uSeen user testing and commercialization preparation.",
                },
                {
                    phase: "Phase 03",
                    title: "Revenue infrastructure",
                    description:
                        "Subscription, licensing and payment infrastructure setup.",
                },
                {
                    phase: "Phase 04",
                    title: "New product releases",
                    description:
                        "Promlock launch and CodUpp development process.",
                },
                {
                    phase: "Phase 05",
                    title: "Global expansion",
                    description:
                        "English versions, global market testing and investor meetings.",
                },
            ],
        },
        team: {
            label: "05 — Team",
            title: "Founding Team",
            intro: "A complementary leadership team covering product, finance and growth.",
            members: [
                {
                    name: "Koray Sefertaş",
                    initials: "KS",
                    role: "Product Vision & Development",
                    description:
                        "Product ideas, product development, product strategy and overall vision.",
                    gradient: "from-cyan-400/30 via-blue-500/20 to-purple-600/20",
                },
                {
                    name: "Çağdaş Oran",
                    initials: "ÇO",
                    role: "Finance & Funding",
                    description: "Finance, funding, budgeting and financial planning.",
                    gradient: "from-blue-500/30 via-indigo-500/20 to-cyan-400/20",
                },
                {
                    name: "Özgür Özerbay",
                    initials: "ÖÖ",
                    role: "Growth & Investor Relations",
                    description:
                        "Investor relations, marketing, sales and external partnerships.",
                    gradient: "from-purple-500/30 via-cyan-400/20 to-blue-500/20",
                },
            ],
        },
        contact: {
            label: "06 — Contact",
            title: "Contact Us",
            intro: "Let's build the next generation of AI-powered digital products.",
            fullName: "Full Name",
            email: "Email",
            message: "Message",
            namePlaceholder: "Your full name",
            emailPlaceholder: "you@example.com",
            messagePlaceholder: "How can we help?",
            submit: "Send Message",
            sending: "Sending...",
            errors: {
                name: "Please enter your full name.",
                emailRequired: "Please enter your email.",
                emailInvalid: "Please enter a valid email address.",
                message: "Please enter a message (at least 10 characters).",
            },
            successTitle: "Thank you.",
            successMessage: "Your message has been received.",
            sendAnother: "Send another message",
        },
        footer: {
            tagline: "AI-powered software and digital product studio.",
            navigate: "Navigate",
            company: "Company",
            focus: "Focus",
            focusItems: ["AI", "SaaS", "Social Apps", "Digital Games"],
            copyright: "© 2026 Sybrax. All rights reserved.",
            closing: "Designed for the next generation of AI-powered products.",
        },
    },

    tr: {
        nav: {
            items: [
                { label: "Hakkında", href: "#about" },
                { label: "Ürünler", href: "#products" },
                { label: "Neden Sybrax", href: "#why-sybrax" },
                { label: "Yol Haritası", href: "#roadmap" },
                { label: "Ekip", href: "#team" },
                { label: "İletişim", href: "#contact" },
            ],
            cta: "Bize Ulaşın",
        },
        hero: {
            eyebrow: "AI ürün stüdyosu · İstanbul",
            title: "Sybrax",
            tagline: "Yapay zeka destekli yazılım ve dijital ürün stüdyosu.",
            description:
                "Sybrax, yapay zeka destekli dijital ürünler geliştiren yeni nesil bir teknoloji girişimidir. İlk ürünlerimiz Mooddy ve uSeen MVP aşamasına ulaşmış olup, Promlock ve CodUpp da yakında portföyümüze katılacaktır.",
            ctaPrimary: "Ürünleri Keşfet",
            ctaSecondary: "Bize Ulaşın",
            emphasis: "AI, SaaS, sosyal ürünler ve dijital oyunlar için geliştirildi.",
        },
        stats: [
            { value: "4", label: "Kullanıma Hazır" },
            { value: "2", label: "Geliştirme Sürecinde" },
            { value: null, label: "AI / SaaS / Sosyal / Oyun" },
            { value: null, label: "Global Odak" },
        ],
        about: {
            label: "01 — Hakkında",
            title: "Sybrax Hakkında",
            note: "Ürün ve pazar deneyimi kanıtlanmış, deneyimli bir kurucu ekip tarafından kuruluyor.",
            p1: "Sybrax, daha önce yatırım almış ve faaliyetlerine devam eden SearchandWork girişiminin ürün geliştirme, pazar deneyimi ve girişimcilik birikimi üzerine kurulan yeni bir yazılım ve teknoloji şirketidir.",
            p2: "SearchandWork, üniversite öğrencileri ile işverenleri buluşturarak öğrencilerin günlük ve dönemsel iş fırsatlarına erişmesini sağlayan bir destek ve istihdam platformudur. Sybrax, bu deneyimin üzerine yapay zeka tabanlı yazılım ürünleri, SaaS platformları, mobil uygulamalar, sosyal dijital ürünler ve dijital oyun geliştirme odağıyla konumlanmaktadır.",
            p3: "Sybrax'in hedefi, tek bir ürüne bağlı kalmadan, yapay zekanın gelişimine paralel olarak farklı kategorilerde global potansiyele sahip dijital ürünler geliştiren esnek ve dayanıklı bir teknoloji şirketi olmaktır.",
        },
        products: {
            label: "02 — Portföy",
            title: "Ürün Portföyü",
            intro: "Sybrax, global potansiyele sahip yapay zeka destekli platformlar, sosyal ürünler ve dijital oyunlar geliştirir.",
            statuses: {
                ready: "Hazır",
                inDevelopment: "Geliştiriliyor",
                comingSoon: "Çok Yakında",
            },
            items: [
                {
                    slug: "mooddy",
                    name: "Mooddy",
                    statusKey: "ready",
                    title: "Yapay zeka destekli oda, karar ve üretkenlik platformu.",
                    description:
                        "Mooddy, çok kullanıcılı konuşmaları özetlemek, kararları ortaya çıkarmak, açık soruları yönetmek, aksiyonları takip etmek ve bilgiyi düzenlemek için yapay zeka kullanan oda bazlı bir üretkenlik platformudur. Dağınık grup konuşmalarını takip edilebilir karar ve aksiyonlara dönüştürür.",
                    link: { href: "https://mooddy.app", label: "Mooddy'yi Ziyaret Et" },
                },
                {
                    slug: "useen",
                    name: "uSeen",
                    statusKey: "ready",
                    title: "Yapay zeka görünürlük ve optimizasyon platformu.",
                    description:
                        "uSeen, markaların ve işletmelerin yapay zeka motorlarında ne kadar görünür olduğunu analiz eder, görünürlüğü skorlar ve iyileştirme görevleri üretir. Bir sonraki fazda, kullanıcı izniyle seçili görevleri kullanıcı adına uygulayabilen bir ajana dönüşmesi hedeflenmektedir.",
                    link: { href: "https://useen.ai", label: "uSeen'i Ziyaret Et" },
                },
                {
                    slug: "work-analyse",
                    name: "Work Analyse",
                    statusKey: "ready",
                    title: "Yapay zekâ destekli iş gücü optimizasyon platformu.",
                    description:
                        "İşletmelerin personel ihtiyacını, iş yoğunluğunu ve farklı istihdam modellerini analiz ederek en verimli iş gücü yapısını ve olası maliyet avantajlarını belirleyen yapay zekâ destekli iş gücü optimizasyon platformu.",
                    link: { href: "https://workanalyse.com", label: "Work Analyse'ı Ziyaret Et" },
                },
                {
                    slug: "abonelikiptal",
                    name: "Abonelikiptal.com",
                    statusKey: "ready",
                    title: "Abonelik ve üyelik iptal platformu.",
                    description:
                        "Abonelik ve üyelik iptal süreçlerini sadeleştiren, kullanıcıların iptal adımlarını hızlı ve anlaşılır şekilde tamamlamasına yardımcı olan dijital platform.",
                    link: { href: "https://abonelikiptal.com", label: "Abonelikiptal.com'u Ziyaret Et" },
                },
                {
                    slug: "codupp",
                    name: "CodUpp",
                    statusKey: "inDevelopment",
                    title: "Yapay zekâ destekli Zero-to-Site platformu.",
                    description:
                        "Basit bir işletme briefini yönlendirmeli bir soru-cevap deneyimiyle profesyonel ve yayına hazır bir web sitesine dönüştüren yapay zekâ destekli Zero-to-Site platformu.",
                },
                {
                    slug: "promlock",
                    name: "Promlock",
                    statusKey: "comingSoon",
                    title: "Sosyal söz ve hesap verebilirlik platformu.",
                    description:
                        "Promlock, kullanıcıların verdikleri sözleri kaydetmesini, paylaşmasını ve kanıtlamasını sağlayarak kişisel taahhütleri sosyal olarak takip edilebilir ve doğrulanabilir hale getirir. Ürün geliştirme süreci aktif olarak devam etmektedir.",
                },
            ],
        },
        why: {
            label: "03 — Neden Sybrax",
            title: "Neden Sybrax?",
            intro: "Sybrax, tek bir ürün fikri olarak konumlanmıyor. Yapay zeka destekli dijital ürünleri farklı dikeylerde geliştirebilen, test edebilen ve ticarileştirebilen bir ürün stüdyosu olarak tasarlandı.",
            features: [
                {
                    key: "portfolio",
                    title: "MVP seviyesinde ürün portföyü",
                    description:
                        "İki ürün halihazırda MVP olgunluğunda; aktif geliştirme hattı portföyü genişletmeye devam ediyor.",
                },
                {
                    key: "ai-saas",
                    title: "AI ve SaaS odaklı yapı",
                    description:
                        "Şirket mimarisi; yapay zeka tabanlı yazılım, SaaS ekonomisi ve tekrarlayan gelir modelleri üzerine kurulu.",
                },
                {
                    key: "speed",
                    title: "Hızlı şirketleşme ve ticarileşme",
                    description:
                        "Konseptten gelire en az organizasyonel sürtünmeyle geçmek için tasarlanmış yalın bir operasyonel yapı.",
                },
                {
                    key: "global",
                    title: "Global pazar potansiyeli",
                    description:
                        "Ürünler, belirli bir coğrafyayla sınırlandırılmadan, ölçeklenebilir pazar potansiyeli gözetilerek geliştirilir.",
                },
            ],
        },
        roadmap: {
            label: "04 — Yol Haritası",
            title: "Yol Haritası",
            intro: "Şirket kuruluşundan global pazar çekişine uzanan net bir yol.",
            stages: [
                {
                    phase: "Phase 01",
                    title: "Şirket kuruluşu",
                    description:
                        "Sybrax şirket kuruluşu ve Cube Incubation / Teknopark başvuru süreci.",
                },
                {
                    phase: "Phase 02",
                    title: "Kullanıcı testleri ve ticarileşme hazırlığı",
                    description:
                        "Mooddy ve uSeen kullanıcı testleri ve ticarileşme hazırlığı.",
                },
                {
                    phase: "Phase 03",
                    title: "Gelir altyapısı",
                    description:
                        "Abonelik, lisanslama ve ödeme altyapısının kurulması.",
                },
                {
                    phase: "Phase 04",
                    title: "Yeni ürün lansmanları",
                    description:
                        "Promlock lansmanı ve CodUpp geliştirme süreci.",
                },
                {
                    phase: "Phase 05",
                    title: "Global genişleme",
                    description:
                        "İngilizce versiyonlar, global pazar testleri ve yatırımcı görüşmeleri.",
                },
            ],
        },
        team: {
            label: "05 — Ekip",
            title: "Kurucu Ekip",
            intro: "Ürün, finans ve büyümeyi kapsayan, birbirini tamamlayan bir liderlik ekibi.",
            members: [
                {
                    name: "Koray Sefertaş",
                    initials: "KS",
                    role: "Ürün Vizyonu ve Geliştirme",
                    description:
                        "Ürün fikirleri, ürün geliştirme, ürün stratejisi ve genel vizyon.",
                    gradient: "from-cyan-400/30 via-blue-500/20 to-purple-600/20",
                },
                {
                    name: "Çağdaş Oran",
                    initials: "ÇO",
                    role: "Finans ve Fonlama",
                    description: "Finans, fonlama, bütçe ve mali planlama.",
                    gradient: "from-blue-500/30 via-indigo-500/20 to-cyan-400/20",
                },
                {
                    name: "Özgür Özerbay",
                    initials: "ÖÖ",
                    role: "Büyüme ve Yatırımcı İlişkileri",
                    description:
                        "Yatırımcı ilişkileri, pazarlama, satış ve dış ilişkiler.",
                    gradient: "from-purple-500/30 via-cyan-400/20 to-blue-500/20",
                },
            ],
        },
        contact: {
            label: "06 — İletişim",
            title: "Bize Ulaşın",
            intro: "Yeni nesil yapay zeka destekli dijital ürünleri birlikte geliştirelim.",
            fullName: "Ad Soyad",
            email: "E-posta",
            message: "Mesaj",
            namePlaceholder: "Adınız ve soyadınız",
            emailPlaceholder: "siz@ornek.com",
            messagePlaceholder: "Size nasıl yardımcı olabiliriz?",
            submit: "Mesaj Gönder",
            sending: "Gönderiliyor...",
            errors: {
                name: "Lütfen adınızı ve soyadınızı girin.",
                emailRequired: "Lütfen e-posta adresinizi girin.",
                emailInvalid: "Lütfen geçerli bir e-posta adresi girin.",
                message: "Lütfen bir mesaj girin (en az 10 karakter).",
            },
            successTitle: "Teşekkürler.",
            successMessage: "Mesajınız alındı.",
            sendAnother: "Başka bir mesaj gönder",
        },
        footer: {
            tagline: "Yapay zeka destekli yazılım ve dijital ürün stüdyosu.",
            navigate: "Site Haritası",
            company: "Şirket",
            focus: "Odak Alanları",
            focusItems: ["AI", "SaaS", "Sosyal Uygulamalar", "Dijital Oyunlar"],
            copyright: "© 2026 Sybrax. Tüm hakları saklıdır.",
            closing: "Yeni nesil yapay zeka destekli ürünler için tasarlandı.",
        },
    },
};
